import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useKidAuth } from '../../contexts/KidAuthContext';
import { supabase, supabaseKid } from '../../services/supabase';
import { bindPush, openPushNotification, pushRpc, syncPush } from '../../services/pushNotifications';

export const PUSH_DESTINATIONS = {
  parent: { home: '/parent', savings: '/parent', chores: '/parent/chores/manage', activity: '/parent/transactions' },
  kid: { home: '/kid/dashboard', savings: '/kid/savings', chores: '/kid/chores', activity: '/kid/reports' },
};
function Runtime({ audience, userId, ready, client }) {
  const location = useLocation(), navigate = useNavigate();
  const [message, setMessage] = useState(null), [error, setError] = useState(''), [notice, setNotice] = useState(null);
  useEffect(() => {
    if (!ready || !userId) return;
    let disposed = false, unbind, resume, lastUsage = 0;
    const usage = () => {
      if (disposed || document.visibilityState !== 'visible' || Date.now()-lastUsage<60000) return;
      lastUsage = Date.now();
      pushRpc(client, 'push_usage', { p_audience: audience }).catch(() => { lastUsage = 0; });
    };
    const returned = () => { usage(); syncPush().catch(() => {}); };
    usage();
    bindPush({ client, audience, isActive: () => !disposed, onForeground: (m,d) => { if (!disposed) setNotice({message:m,data:d}); }, onMessage: m => { if (!disposed) setMessage(m); }, onError: e => { if (!disposed) setError(e); } })
      .then(fn => { if (disposed) fn(); else unbind = fn; }).catch(() => {});
    document.addEventListener('visibilitychange', returned);
    window.addEventListener('focus', returned);
    // Pointer/key activity counts an actively used long-running session, not a background timer.
    document.addEventListener('pointerdown', usage);
    document.addEventListener('keydown', usage);
    if (Capacitor.isNativePlatform()) App.addListener('appStateChange', ({ isActive }) => { if (isActive) returned(); })
      .then(handle => { if (disposed) handle.remove(); else resume = handle; });
    return () => { disposed = true; unbind?.(); resume?.remove(); document.removeEventListener('visibilitychange', returned);
      window.removeEventListener('focus', returned); document.removeEventListener('pointerdown', usage); document.removeEventListener('keydown', usage);
      };
  }, [audience, userId, ready, client]);
  // A same-account profile refresh must not erase an opened notification.
  useEffect(() => { setMessage(null); setError(''); setNotice(null); }, [audience, userId]);
  useEffect(() => {
    if (ready && userId && document.visibilityState === 'visible') pushRpc(client, 'push_usage', { p_audience: audience }).catch(() => {});
  }, [location.pathname, audience, userId, ready, client]);
  if (!ready || !userId) return null;
  if (!message && !error) return notice ? createPortal(<aside
    className="fixed inset-x-4 z-[200] mx-auto max-w-md overflow-y-auto rounded-xl border bg-white p-4 text-gray-900 shadow-xl"
    style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)', maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 1.5rem)' }}
    role="status" aria-live="polite" aria-atomic="true">
    <strong>{notice.message.title}</strong><p>{notice.message.body.slice(0,180)}</p>
    <button className="mr-4 mt-2 font-semibold text-purple-700" onClick={() => { openPushNotification(notice.data); setNotice(null); }}>Read notification</button>
    <button onClick={() => setNotice(null)}>Dismiss</button>
  </aside>, document.body) : null;
  return createPortal(<div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-5" role="dialog" aria-modal="true" aria-label="MyPiggy notification">
    <div className="w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 text-gray-900 shadow-xl"
      style={{ maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 2.5rem)' }}>
      <h2 className="text-lg font-bold">{message?.title || 'MyPiggy notification'}</h2>
      <p className="mt-3 whitespace-pre-wrap">{message?.body || error}</p>
      {message && <button className="mt-5 rounded-lg bg-purple-700 px-4 py-2 text-white" onClick={() => {
        const route = PUSH_DESTINATIONS[audience][message.destination]; setMessage(null); if (route) navigate(route);
      }}>Open MyPiggy</button>}
      <button className="ml-3 mt-5 px-3 py-2" onClick={() => { setMessage(null); setError(''); }}>Close</button>
    </div>
  </div>, document.body);
}
export function ParentPushRuntime() {
  const { user, parent, initializing, parentChecked } = useAuth();
  return <Runtime audience="parent" userId={user?.id} ready={!initializing && parentChecked && !!parent} client={supabase} />;
}
export function KidPushRuntime() {
  const { kidUser, currentKid, loading, kidChecked } = useKidAuth();
  return <Runtime audience="kid" userId={kidUser?.id} ready={!loading && kidChecked && !!currentKid} client={supabaseKid} />;
}
